import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icon.js';
import { supabase } from '../supabase.js';
import TabPrivacySettings from './TabPrivacySettings.js';
import { calculateLevelProgress } from '../utils/xpLevelUtils';
import AchievementsCompact from './AchievementsCompact.js';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import BottomNavigation from './BottomNavigation.js';
import './UserProfilePage.css';

function UserProfilePage({ 
  user, 
  currentUserId,
  activity, 
  protocols, 
  posts, 
  onProfileToggle, 
  onProfilePictureUpdate,
  onFollowersClick,
  onFollowingClick,
  onMessage,
  onFollowToggle,
  isFollowing = false,
  isFollowPending = false,
  followLoading = false,
  onSettingsClick,
  children, // This will contain the content sections
  tabPrivacySettings = {}, // Privacy settings for each tab
  visibleTabs = ['activity', 'protocols', 'posts', 'biomarkers', 'challenges', 'wearables'], // Tabs that can be viewed
  onTabPrivacyChange // Callback when tab privacy changes
}) {
  const [activeTab, setActiveTab] = useState('activity');
  const navigate = useNavigate();
  const isOwnProfile = user?.id === currentUserId || user?.user_id === currentUserId;
  
  // Calculate stats
  const activeProtocolsCount = protocols?.filter(p => p.status === 'active').length || 0;
  const [biomarkersCount, setBiomarkersCount] = useState(0);
  const [challengesCompletedCount, setChallengesCompletedCount] = useState(0);
  const [protocolUsersCount, setProtocolUsersCount] = useState(0);
  const [loggingStreakDays, setLoggingStreakDays] = useState(0);
  const [reportsGenerated, setReportsGenerated] = useState(0);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  
  // Close settings menu when clicking outside
  useEffect(() => {
    if (showSettingsMenu) {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.user-profile-settings-menu') && 
            !event.target.closest('.user-profile-secondary-action')) {
          setShowSettingsMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettingsMenu]);

  // Close tips modal when clicking outside
  useEffect(() => {
    if (showTipsModal) {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.user-profile-tips-modal') && 
            !event.target.closest('.user-profile-tips-button')) {
          setShowTipsModal(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTipsModal]);

  // Close avatar modal when clicking outside
  useEffect(() => {
    if (showAvatarModal) {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.user-profile-avatar-modal')) {
          setShowAvatarModal(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      // Also close on Escape key
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          setShowAvatarModal(false);
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showAvatarModal]);


  // Handle hash navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      // Extract the tab name (before any query parameters)
      const tabName = hash.split('?')[0].split('&')[0];
      if (tabName && ['activity', 'protocols', 'posts', 'biomarkers', 'challenges', 'wearables'].includes(tabName)) {
        setActiveTab(tabName);
      }
    };

    // Check on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  
  // Fallback values if user data is not available
  const displayName = user?.username || 'User';
  const location = user?.location || 'Location not set';
  const bio = user?.bio || '';
  const followersCount = user?.followers_count || 0;
  
  // Check if bio is generic or empty
  const isGenericBio = (bioText) => {
    if (!bioText || bioText.trim() === '') return true;
    const genericBios = [
      "Hey there! I'm new here.",
      "Hey there! I'm new here",
      "I'm new here.",
      "I'm new here",
      "New here.",
      "New here"
    ];
    return genericBios.some(generic => 
      bioText.trim().toLowerCase() === generic.toLowerCase()
    );
  };
  
  const hasMeaningfulBio = bio && !isGenericBio(bio);
  const followingCount = user?.following_count || 0;
  const profileImage = user?.avatar_url || '/default-avatar.jpg';
  const followers = user?.followers || [];
  const following = user?.following || [];
  const level = user?.user_level ?? user?.level ?? 1;
  const xp = user?.xp ?? 0;
  const country = user?.country || (location !== 'Location not set' ? location : null);
  
  // Helper function to convert country name to country code
  const getCountryCode = (countryName) => {
    if (!countryName || countryName === 'Location not set') return '';
    
    // If it's already a 2-letter code, return it uppercase
    if (countryName.length === 2 && /^[A-Za-z]{2}$/.test(countryName)) {
      return countryName.toUpperCase();
    }
    
    // Map of common country names to ISO 3166-1 alpha-2 codes
    const countryToCode = {
      'United States': 'US', 'United States of America': 'US',
      'Canada': 'CA',
      'United Kingdom': 'GB', 'UK': 'GB',
      'Australia': 'AU',
      'Germany': 'DE',
      'France': 'FR',
      'Italy': 'IT',
      'Spain': 'ES',
      'Netherlands': 'NL',
      'Belgium': 'BE',
      'Switzerland': 'CH',
      'Austria': 'AT',
      'Sweden': 'SE',
      'Norway': 'NO',
      'Denmark': 'DK',
      'Finland': 'FI',
      'Poland': 'PL',
      'Portugal': 'PT',
      'Greece': 'GR',
      'Ireland': 'IE',
      'Czech Republic': 'CZ',
      'Romania': 'RO',
      'Hungary': 'HU',
      'Croatia': 'HR',
      'Japan': 'JP',
      'China': 'CN',
      'India': 'IN',
      'South Korea': 'KR',
      'Brazil': 'BR',
      'Mexico': 'MX',
      'Argentina': 'AR',
      'Chile': 'CL',
      'Colombia': 'CO',
      'Peru': 'PE',
      'South Africa': 'ZA',
      'Egypt': 'EG',
      'Nigeria': 'NG',
      'Kenya': 'KE',
      'Israel': 'IL',
      'Turkey': 'TR',
      'Russia': 'RU',
      'Ukraine': 'UA',
      'New Zealand': 'NZ',
      'Singapore': 'SG',
      'Malaysia': 'MY',
      'Thailand': 'TH',
      'Indonesia': 'ID',
      'Philippines': 'PH',
      'Vietnam': 'VN',
      'Saudi Arabia': 'SA',
      'United Arab Emirates': 'AE',
      'Pakistan': 'PK',
      'Bangladesh': 'BD',
    };
    
    // Try exact match (case-insensitive)
    const match = Object.keys(countryToCode).find(
      key => key.toLowerCase() === countryName.toLowerCase()
    );
    
    if (match) {
      return countryToCode[match];
    }
    
    // Fallback: take first 2 uppercase letters if it's a long name
    if (countryName.length >= 2) {
      return countryName.substring(0, 2).toUpperCase();
    }
    
    return countryName.toUpperCase();
  };
  
  const countryCode = getCountryCode(country);
  
  // State for level tooltip
  const [levelProgress, setLevelProgress] = useState(null);
  const [showLevelTooltip, setShowLevelTooltip] = useState(false);
  
  // State for stats tooltips
  const [showFollowersTooltip, setShowFollowersTooltip] = useState(false);
  const [showFollowingTooltip, setShowFollowingTooltip] = useState(false);
  const [showCountryTooltip, setShowCountryTooltip] = useState(false);
  
  // Load level progress data for tooltip
  useEffect(() => {
    const loadTooltipData = async () => {
      const progress = await calculateLevelProgress(xp, level);
      setLevelProgress(progress);
    };
    
    if (xp !== undefined && level !== undefined) {
      loadTooltipData();
    }
  }, [xp, level]);
  
  // Load biomarkers, challenges, logging streak, and reports counts (only when viewer has access)
  useEffect(() => {
    const loadStats = async () => {
      if (!user?.id && !user?.user_id) return;
      // Don't fetch sensitive stats until we know viewer can see them (avoids leaking data in network)
      if (visibleTabs === null) return;
      if (!isOwnProfile && (!visibleTabs || visibleTabs.length === 0)) return;
      const userId = user?.id || user?.user_id;

      try {
        // Count biomarkers with results
        const { count: biomarkersCount } = await supabase
          .from('biomarker_panel_results')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);
        setBiomarkersCount(biomarkersCount || 0);
        
        // Count completed challenges
        const countCacheKey = getCacheKey('countCompletedChallenges', userId);
        const challengesCount = await dedupeRequest(countCacheKey, async () => {
          const { count, error } = await supabase
            .from('user_challenge_progress')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('completed', true);
          if (error) throw error;
          return count;
        });
        setChallengesCompletedCount(challengesCount || 0);

        // Calculate logging streak days
        const { data: logEntries } = await supabase
          .from('logging_hack_entries')
          .select('date')
          .eq('user_id', userId)
          .order('date', { ascending: false });
        
        if (logEntries && logEntries.length > 0) {
          // Get unique dates, sorted descending
          const uniqueDates = [...new Set(logEntries.map(e => e.date))].map(d => {
            const date = new Date(d);
            date.setHours(0, 0, 0, 0);
            return date.getTime();
          }).sort((a, b) => b - a);
          
          // Calculate streak from today backwards
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayTime = today.getTime();
          
          let streak = 0;
          let checkDate = new Date(today);
          
          // Start checking from today or yesterday
          // If most recent log is today, start from today
          // If most recent log is yesterday, start from yesterday
          // Otherwise, no streak
          if (uniqueDates.length > 0) {
            const mostRecentLog = uniqueDates[0];
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayTime = yesterday.getTime();
            
            if (mostRecentLog === todayTime) {
              // Log exists today, start counting from today
              checkDate = new Date(today);
            } else if (mostRecentLog === yesterdayTime) {
              // Most recent log is yesterday, start from yesterday
              checkDate = new Date(yesterday);
            } else {
              // Most recent log is older, no active streak
              setLoggingStreakDays(0);
              return;
            }
            
            // Count consecutive days backwards
            for (const logTime of uniqueDates) {
              const checkTime = checkDate.getTime();
              if (logTime === checkTime) {
                streak++;
                checkDate.setDate(checkDate.getDate() - 1);
              } else if (logTime < checkTime) {
                // Gap found, streak broken
                break;
              }
              // If logTime > checkTime, we've already passed this date, continue
            }
          }
          
          setLoggingStreakDays(streak);
        } else {
          setLoggingStreakDays(0);
        }

        // Count reports generated
        const { count: reportsCount } = await supabase
          .from('protocol_report_snapshots')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);
        setReportsGenerated(reportsCount || 0);
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    };
    
    loadStats();
  }, [user?.id, user?.user_id, visibleTabs, isOwnProfile]);

  // Load protocol users count (only when viewer has access)
  useEffect(() => {
    const loadProtocolUsersCount = async () => {
      if (!user?.id && !user?.user_id) return;
      if (visibleTabs === null) return;
      if (!isOwnProfile && (!visibleTabs || visibleTabs.length === 0)) return;
      const profileUserId = user?.id || user?.user_id;

      try {
        // First, get all protocol IDs created by this user - use deduplication
        const protocolsCacheKey = getCacheKey('fetchUserCreatedProtocolIds', profileUserId);
        const userProtocols = await dedupeRequest(protocolsCacheKey, async () => {
          const { data, error } = await supabase
            .from('protocols')
            .select('id')
            .eq('created_by', profileUserId);
          
          if (error) {
            console.error('Error fetching user protocols:', error);
            throw error;
          }
          return data;
        });
        
        if (!userProtocols) {
          return;
        }
        
        if (!userProtocols || userProtocols.length === 0) {
          setProtocolUsersCount(0);
          return;
        }
        
        const protocolIds = userProtocols.map(p => p.id);
        
        // Count unique user_ids in protocols_status where is_active is true
        // for protocols created by this user
        const { data: activeStatuses, error: statusError } = await supabase
          .from('protocols_status')
          .select('user_id')
          .in('protocol_id', protocolIds)
          .eq('is_active', true);
        
        if (statusError) {
          console.error('Error fetching protocol statuses:', statusError);
          return;
        }
        
        // Count unique user_ids
        const uniqueUserIds = new Set(
          (activeStatuses || []).map(status => status.user_id).filter(Boolean)
        );
        
        setProtocolUsersCount(uniqueUserIds.size);
      } catch (error) {
        console.error('Error loading protocol users count:', error);
      }
    };
    
    loadProtocolUsersCount();
  }, [user?.id, user?.user_id, visibleTabs, isOwnProfile]);
  
  // Calculate total users of protocols created by this user
  const totalProtocolUsers = protocols?.reduce((total, protocol) => {
    return total + (protocol.users_count || protocol.protocols_status?.length || 0);
  }, 0) || 0;
  
  // Get first active protocol for CTA
  const firstActiveProtocol = protocols?.find(p => p.status === 'active');
  
  // Handle primary CTA
  const handlePrimaryCTA = () => {
    if (firstActiveProtocol) {
      // Navigate to protocols tab
      const userId = user?.id || user?.user_id;
      navigate(`/user${userId ? `/${userId}` : ''}#protocols`);
      setActiveTab('protocols');
    } else {
      // Navigate to create protocol
      navigate('/protocols/create');
    }
  };
  
  // Check if should show social stats (threshold: >= 10)
  const shouldShowSocialStats = followersCount >= 10 || followingCount >= 10 || isOwnProfile;
  
  // State for profile visibility toggle
  const [publicProfile, setPublicProfile] = useState(user?.public_profile !== false);
  
  // State for profile picture editing (removed - now handled in Edit Profile modal)
  
  // State for bio editing
  const [showBioEditor, setShowBioEditor] = useState(false);
  const [editedBio, setEditedBio] = useState(bio);
  const [savingBio, setSavingBio] = useState(false);

  // Sync edited bio with prop changes when not editing
  useEffect(() => {
    if (!showBioEditor) {
      setEditedBio(user?.bio || '');
    }
  }, [user?.bio, showBioEditor]);

  // Profile picture state removed - now handled in Edit Profile modal

  // Handle profile toggle
  const handleProfileToggle = () => {
    const newPublicProfile = !publicProfile;
    setPublicProfile(newPublicProfile);
    
    // Call the parent component's callback if provided
    if (onProfileToggle) {
      onProfileToggle(newPublicProfile);
    }
  };

  // Function to delete old profile picture
  const deleteOldProfilePicture = async (userId) => {
    try {
      // Get all files in the user-profile-pictures bucket
      const { data: files, error: listError } = await supabase.storage
        .from('user-profile-pictures')
        .list('', {
          search: `profile-${userId}-`
        });

      if (listError) {
        console.error('Error listing files:', listError);
        return;
      }

      if (files && files.length > 0) {
        // Filter files that match the exact user ID pattern
        const userFiles = files.filter(file => 
          file.name.startsWith(`profile-${userId}-`)
        );
        
        if (userFiles.length > 0) {
          const filesToDelete = userFiles.map(file => file.name);
          
          const { error: deleteError } = await supabase.storage
            .from('user-profile-pictures')
            .remove(filesToDelete);

          if (deleteError) {
            console.error('Error deleting old profile pictures:', deleteError);
          } else {
            console.log('Deleted old profile pictures:', filesToDelete);
          }
        }
      }
    } catch (error) {
      console.error('Error in deleteOldProfilePicture:', error);
    }
  };

  // Profile picture functions removed - now handled in Edit Profile modal

  // All profile picture functions removed - now handled in Edit Profile modal

  // Handle bio editor toggle
  const handleBioEditorToggle = () => {
    if (!isOwnProfile) return;
    setShowBioEditor(!showBioEditor);
    if (!showBioEditor) {
      setEditedBio(bio); // Reset to current bio when opening
    }
  };

  // Handle bio change
  const handleBioChange = (event) => {
    setEditedBio(event.target.value);
  };

  // Handle bio save
  const handleBioSave = async () => {
    if (!isOwnProfile || savingBio) return;
    
    setSavingBio(true);
    try {
      const userId = user?.id || user?.user_id;
      if (!userId) {
        throw new Error('No user ID available');
      }

      const bioToSave = editedBio.trim() || null;

      const { error } = await supabase
        .from('user_profiles')
        .update({ bio: bioToSave })
        .eq('user_id', userId);

      if (error) {
        console.error('Bio update error:', error);
        throw new Error('Failed to update bio. Please try again.');
      }

      // Close editor
      setShowBioEditor(false);
      
      // Reload the page to refresh user data from parent
      window.location.reload();
    } catch (error) {
      console.error('Error updating bio:', error);
      alert(getUserFacingErrorMessage(error, 'Failed to update bio. Please try again.'));
    } finally {
      setSavingBio(false);
    }
  };

  // Handle bio cancel
  const handleBioCancel = () => {
    setShowBioEditor(false);
    setEditedBio(bio); // Reset to current bio
  };

  // Handle keyboard shortcuts
  const handleBioKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleBioSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleBioCancel();
    }
  };

  // Helper function to get tab icon based on privacy level
  const getTabIcon = (tab) => {
    // Tab-specific icons for navigation bar (privacy icons shown on content pages only)
    switch (tab) {
      case 'activity': return 'Activity';
      case 'protocols': return 'TrendingUp';
      case 'biomarkers': return 'TestTube';
      case 'wearables': return 'Heart';
      case 'posts': return 'FileText';
      case 'challenges': return 'Trophy';
      default: return 'Activity';
    }
  };

  // Helper function to get tab label
  const getTabLabel = (tab) => {
    switch (tab) {
      case 'activity': return 'Activity';
      case 'protocols': return 'Protocols';
      case 'biomarkers': return 'Biomarkers';
      case 'wearables': return 'Wearables';
      case 'posts': return 'Posts';
      case 'challenges': return 'Challenges';
      default: return 'Activity';
    }
  };

  // Get content for active tab
  const getTabContent = () => {
    // Tab visibility not yet computed (parent is still checking privacy/follow)
    if (visibleTabs === null) {
      return (
        <div className="user-profile-privacy-notice">
          <div className="user-profile-privacy-notice-content">
            <p className="user-profile-privacy-description">Loading…</p>
          </div>
        </div>
      );
    }
    // Check if current tab is visible
    if (!visibleTabs.includes(activeTab)) {
      const privacyLevel = tabPrivacySettings[activeTab] || 'followers_only';
      const tabLabel = getTabLabel(activeTab);
      
      let title = 'This Tab is Private';
      let description = `This user's ${tabLabel.toLowerCase()} are only visible to them.`;
      
      if (privacyLevel === 'followers_only') {
        title = 'This Tab is Followers Only';
        description = `This user's ${tabLabel.toLowerCase()} are only visible to their followers.`;
      } else if (privacyLevel === 'private') {
        title = 'This Tab is Private';
        description = `This user's ${tabLabel.toLowerCase()} are only visible to them.`;
      }
      
      return (
        <div className="user-profile-privacy-notice">
          <div className="user-profile-privacy-notice-content">
            <div className="user-profile-privacy-icon">
              <Icon name="Lock" />
            </div>
            <h3 className="user-profile-privacy-title">{title}</h3>
            <p className="user-profile-privacy-description">{description}</p>
            {!isFollowing && privacyLevel === 'followers_only' && (
              <button 
                className="user-profile-privacy-follow-btn"
                onClick={onFollowToggle}
                disabled={followLoading}
              >
                {followLoading ? 'Loading...' : 'Follow'}
              </button>
            )}
          </div>
        </div>
      );
    }
    
    if (!children) return <div className="user-profile-empty-state">No content available</div>;
    
    const childrenArray = React.Children.toArray(children);
    
    switch (activeTab) {
      case 'activity':
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-activity-content')
        ) || <div className="user-profile-empty-state">No activity data available</div>;
      case 'protocols':
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-protocols-content')
        ) || <div className="user-profile-empty-state">No protocols available</div>;
      case 'posts':
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-posts-content')
        ) || <div className="user-profile-empty-state">No posts available</div>;
      case 'biomarkers':
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-biomarkers-content')
        ) || <div className="user-profile-empty-state">No biomarkers data available</div>;
      case 'challenges':
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-challenges-content')
        ) || <div className="user-profile-empty-state">No challenges data available</div>;
      case 'wearables':
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-wearables-content')
        ) || <div className="user-profile-empty-state">No wearables data available</div>;
      default:
        return childrenArray.find(child => 
          child.props?.className?.includes('user-profile-activity-content')
        ) || <div className="user-profile-empty-state">No content available</div>;
    }
  };

  // Get tab counts
  const getTabCount = (tab) => {
    switch (tab) {
      case 'activity':
        // Count unique dates with activity
        return activity?.filter(a => a.protocols && a.protocols.length > 0).length || 0;
      case 'protocols':
        return protocols?.length || 0;
      case 'posts':
        return posts?.length || 0;
      case 'biomarkers':
        return biomarkersCount;
      case 'challenges':
        return challengesCompletedCount;
      case 'wearables':
        // For now, return null for wearables (can be implemented later)
        return null;
      default:
        return null;
    }
  };

  // Check if user has any restricted tabs (private or followers_only)
  const hasRestrictedTabs = () => {
    if (isOwnProfile) return false; // Owner always sees everything
    const tabs = ['activity', 'protocols', 'posts', 'biomarkers', 'challenges', 'wearables', 'logging'];
    return tabs.some(tab => {
      const privacyLevel = tabPrivacySettings[tab] || 'followers_only'; // Default is followers_only
      return privacyLevel === 'private' || privacyLevel === 'followers_only';
    });
  };

  // Check if followers/following lists should be visible
  // They should be visible if: owner, OR following the user (and follow is accepted)
  const canViewFollowersFollowing = () => {
    if (isOwnProfile) return true; // Owner always sees everything
    // If user is following (and follow is accepted, not pending), they can see followers/following lists
    return isFollowing && !isFollowPending;
  };

  // Format tab count for display
  const formatTabCount = (count, tab) => {
    if (count === null || count === undefined) {
      return null;
    }
    
    // Owner always sees counts
    if (isOwnProfile) {
      return count > 0 ? count : null;
    }
    
    // For other users, check privacy settings
    const tabPrivacy = tabPrivacySettings[tab] || 'followers_only';
    
    // Hide count if tab is private (only owner can see)
    if (tabPrivacy === 'private') {
      return null;
    }
    
    // Hide count if tab is followers_only and user is not following OR follow is pending
    if (tabPrivacy === 'followers_only') {
      // Only show count if following AND follow is accepted (not pending)
      if (!isFollowing || isFollowPending) {
        return null; // Hide count if not following or follow is pending
      }
    }
    
    // Public tabs or accepted followers can see counts
    return count > 0 ? count : null;
  };

  // Get tips content based on active tab
  const getTipsContent = () => {
    switch (activeTab) {
      case 'activity':
        return isOwnProfile ? (
          <div>
            <p className="tips-intro">Track your daily protocol and biohack logging activity with this visual timeline:</p>
            <ul>
              <li><strong>Click on any day</strong> to see detailed logs of what biohacks and protocols you completed</li>
              <li><strong>Navigate through different weeks</strong> to review your historical activity</li>
              <li><strong>View statistics</strong> like your weekly averages and most active days</li>
              <li><strong>Click on individual biohacks or protocols</strong> to see charts showing your usage patterns over time</li>
            </ul>
            <p className="tips-outro">
              Use this tab to monitor your consistency, identify trends, and stay motivated by seeing your progress.
            </p>
          </div>
        ) : (
          <div>
            <p className="tips-intro">View this user's activity history and logged biohacks:</p>
            <ul>
              <li><strong>Click on any day</strong> to see what protocols and biohacks they logged</li>
              <li><strong>Navigate through different weeks</strong> to explore their historical activity</li>
              <li><strong>Click on biohacks or protocols</strong> to view usage statistics and patterns</li>
              <li><strong>See their logging consistency</strong> and discover which protocols they follow most actively</li>
            </ul>
          </div>
        );
      case 'protocols':
        return isOwnProfile ? (
          <div>
            <p className="tips-intro">Manage and organize your protocols on this tab:</p>
            <ul>
              <li><strong>Filter protocols</strong> by status using the Active/Staged buttons at the top</li>
              <li><strong>Click on any protocol card</strong> to view detailed information of a biohack</li>
              <li><strong>Use the FAB menu</strong> (three dots icon) on each protocol card for additional actions</li>
            </ul>
            
            <div className="tips-section">
              <p className="tips-section-title">FAB Menu Actions:</p>
              <ul>
                <li><strong>Schedule</strong> — Set up or update a logging schedule for your protocol</li>
                <li><strong>Activate</strong> — Activate a protocol to log and measure metrics for it</li>
                <li><strong>Deactivate</strong> — Pause an active protocol without deleting it</li>
                <li><strong>Notes</strong> — View and edit personal notes for a protocol</li>
                <li><strong>Like / Dislike</strong> — Rate protocols to help others discover what works</li>
                <li><strong>Delete</strong> — Remove a protocol you created (only visible for your own protocols)</li>
                <li><strong>Add to Staged</strong> — Add a protocol to your staged protocols (only visible on other's protocols)</li>
              </ul>
            </div>
            
            <p className="tips-outro">
              Create new protocols by clicking the "Create Protocol" button, or browse and manage your existing ones here.
            </p>
          </div>
        ) : (
          <div>
            <p className="tips-intro">Explore protocols created and used by this profile:</p>
            <ul>
              <li><strong>Filter protocols</strong> by status using the Active/Staged buttons at the top</li>
              <li><strong>Click on any protocol card</strong> to view detailed information</li>
              <li><strong>Browse their active protocols</strong> to see what they're currently following</li>
              <li><strong>Check their staged protocols</strong> to discover protocols they're interested in</li>
              <li><strong>View protocol details</strong> including biohacks, user counts, and ratings</li>
            </ul>
            
            <p className="tips-outro">
              Use this tab to discover protocols and get inspiration for your own health optimization journey.
            </p>
          </div>
        );
      case 'posts':
        return isOwnProfile
          ? 'See the posts you\'ve shared with the community'
          : 'Read posts and updates shared by this profile.';
      case 'biomarkers':
        return isOwnProfile ? (
          <div>
            <p className="tips-intro">Track and manage your biomarker lab results with visual progress indicators:</p>
            <ul>
              <li><strong>Upload panel data</strong> by clicking "Add Panel Data" to record new lab results with multiple biomarkers</li>
              <li><strong>Filter by panel</strong> using the dropdown at the top to view results from specific test dates</li>
              <li><strong>View biomarker cards</strong> showing your current values with color-coded status</li>
              <li><strong>Click the info icon</strong> on any biomarker card to view detailed descriptions and source information</li>
              <li><strong>Browse not-tested biomarkers</strong> in the collapsible section to see what else you could track</li>
              <li><strong>Review historical data</strong> by selecting different panels from the dropdown</li>
            </ul>
            <p className="tips-outro">
              Use this tab to monitor your health metrics over time, identify trends, and track your progress toward optimal biomarker levels.
            </p>
          </div>
        ) : (
          <div>
            <p className="tips-intro">View this user's biomarker lab results and health metrics:</p>
            <ul>
              <li><strong>Filter by panel</strong> using the dropdown at the top to view results from specific test dates</li>
              <li><strong>View biomarker cards</strong> showing their current values with visual progress indicators</li>
              <li><strong>See color-coded status</strong> indicating where values fall within optimal, warning, or critical ranges</li>
              <li><strong>Click the info icon</strong> on any biomarker card to view detailed descriptions and source information</li>
              <li><strong>Browse tested biomarkers</strong> to see what health metrics they're tracking</li>
              <li><strong>Review their historical data</strong> by selecting different panels from the dropdown</li>
              <li><strong>Check not-tested biomarkers</strong> in the collapsible section to see other available metrics</li>
            </ul>
            <p className="tips-outro">
              Use this tab to learn about their health optimization journey and see how they track their biomarkers.
            </p>
          </div>
        );
      case 'challenges':
        return isOwnProfile
          ? 'Join challenges with the community to set goals for yourself and earn rewards'
          : 'View challenges this user has joined and their progress';
      case 'wearables':
        return isOwnProfile ? (
          <div>
            <p className="tips-intro">Track and monitor your sleep data from wearable devices with detailed metrics and visualizations:</p>
            <ul>
              <li><strong>Manual Entry</strong> — Click "Manual Entry" to manually add sleep data when your device isn't synced</li>
              <li><strong>Connect Devices</strong> — Click "Connect" to link your Fitbit account and automatically sync sleep data (Garmin and Whoop coming soon)</li>
              <li><strong>Sync Data</strong> — Use the sync button in the device dropdown to refresh data from connected devices</li>
              <li><strong>View Sleep Metrics</strong> — See total sleep time, sleep score, bed/wake times, and sleep stage breakdown (Deep, REM, Light, Awake)</li>
              <li><strong>Check Sleep Efficiency</strong> — Monitor your sleep efficiency percentage to understand sleep quality</li>
              <li><strong>Select Analysis Date</strong> — Use the dropdown to view data from latest, last week, last month, or other time periods</li>
              <li><strong>View Detailed Charts</strong> — Click the arrow button on the sleep card to open detailed sleep breakdown charts with date range filters</li>
              <li><strong>Device Status</strong> — Check connection status in the top-right dropdown to see which devices are connected</li>
            </ul>
            <p className="tips-outro">
              Use this tab to monitor your sleep patterns over time, identify trends, and optimize your sleep quality through data-driven insights.
            </p>
          </div>
        ) : (
          <div>
            <p className="tips-intro">View this user's sleep data and wearable device metrics:</p>
            <ul>
              <li><strong>View Sleep Metrics</strong> — See their total sleep time, sleep score, bed/wake times, and sleep stage breakdown</li>
              <li><strong>Check Sleep Efficiency</strong> — View their sleep efficiency percentage to understand their sleep quality</li>
              <li><strong>Select Analysis Date</strong> — Use the dropdown to view their data from different time periods (latest, last week, last month, etc.)</li>
              <li><strong>View Detailed Charts</strong> — Click the arrow button on the sleep card to see detailed sleep breakdown charts over time</li>
              <li><strong>See Device Connections</strong> — Check which wearable devices they have connected (Fitbit, Garmin, Whoop)</li>
              <li><strong>Review Historical Data</strong> — Explore their sleep patterns and trends over different time periods</li>
            </ul>
            <p className="tips-outro">
              Use this tab to learn about their sleep tracking journey and see how they monitor their sleep quality and patterns.
            </p>
          </div>
        );
      default:
        return '';
    }
  };

  return (
    <div className="user-profile-page">
      {/* Compact Cover Banner */}
      <div className="user-profile-header-banner">
        <div className="user-profile-banner-content">
        </div>
      </div>

      {/* Compact White Header Banner */}
      <div className="user-profile-white-banner">
        {/* Avatar overlaps cover and white banner */}
        <div className="user-profile-avatar-section">
          <div 
            className="user-profile-avatar user-profile-avatar-clickable"
            onClick={() => setShowAvatarModal(true)}
            style={{ cursor: 'pointer' }}
          >
            {user?.avatar_url && user?.avatar_url !== '/default-avatar.jpg' ? (
              <img 
                src={user.avatar_url} 
                alt={`${displayName}'s profile`}
                className="user-profile-avatar-img"
              />
            ) : (
              <div className="user-profile-avatar-fallback">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>
        <div className="user-profile-white-banner-content">
          {/* Two-column layout: Identity (left) + Actions/Stats (right) */}
          <div className="user-profile-header-main">
            {/* Left: Identity Block */}
            <div className="user-profile-identity-block">
              <div className="user-profile-name-header">
                <h1 className="user-profile-name">
                  {displayName}
                </h1>
              </div>
              
              {/* Social Stats - Show counts but disable clicking if user has restricted tabs (unless viewing own profile or following the user) */}
              <p className="user-profile-social-stats">
                <span 
                  className={`user-profile-social-stat-link ${!canViewFollowersFollowing() ? 'user-profile-social-stat-disabled' : ''}`}
                  onClick={!canViewFollowersFollowing() ? undefined : onFollowersClick}
                >
                  {followersCount} followers
                </span>
                {' · '}
                <span 
                  className={`user-profile-social-stat-link ${!canViewFollowersFollowing() ? 'user-profile-social-stat-disabled' : ''}`}
                  onClick={!canViewFollowersFollowing() ? undefined : onFollowingClick}
                >
                  {followingCount} following
                </span>
              </p>

              {/* Bio */}
              {showBioEditor && isOwnProfile ? (
                <div className="user-profile-bio-editor">
                  <input
                    className="user-profile-bio-input"
                    type="text"
                    value={editedBio}
                    onChange={handleBioChange}
                    onKeyDown={handleBioKeyDown}
                    placeholder="Add a short bio"
                    autoFocus
                  />
                  <div className="user-profile-bio-buttons">
                    <button
                      className="user-profile-bio-save-btn"
                      onClick={handleBioSave}
                      disabled={savingBio}
                      type="button"
                    >
                      {savingBio ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="user-profile-bio-close-btn"
                      onClick={handleBioCancel}
                      disabled={savingBio}
                      type="button"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : hasMeaningfulBio && isOwnProfile ? (
                <p 
                  className="user-profile-bio user-profile-bio-editable"
                  onClick={handleBioEditorToggle}
                >
                  {bio}
                </p>
              ) : hasMeaningfulBio ? (
                <p className="user-profile-bio">{bio}</p>
              ) : isOwnProfile ? (
                <button 
                  className="user-profile-bio user-profile-bio-prompt"
                  onClick={handleBioEditorToggle}
                  type="button"
                >
                  <span className="user-profile-bio-prompt-text">Add a short bio </span>
                  <Icon name="Edit" className="user-profile-bio-prompt-icon" />
                </button>
              ) : (
                <p className="user-profile-bio">Hey! I'm new here.</p>
              )}
            </div>

            {/* Right: Actions + Stats Block */}
            <div className="user-profile-actions-stats-block">
              {/* Top Row: Achievements + Action Buttons */}
              <div className="user-profile-top-row">
                {/* Compact Achievements (combines achievements, level, and XP) */}
                {user?.id || user?.user_id ? (
                  <AchievementsCompact 
                    userId={user?.id || user?.user_id} 
                    isOwnProfile={isOwnProfile}
                    xp={xp}
                    level={level}
                  />
                ) : null}
                
                {/* Action Buttons - Next to achievements */}
                <div className="user-profile-actions-row-compact">
                  {isOwnProfile && (
                    <button
                      className="user-profile-primary-cta-compact"
                      onClick={handlePrimaryCTA}
                    >
                      {firstActiveProtocol ? (
                        <>
                          <Icon name="TrendingUp" />
                          View Protocol
                        </>
                      ) : (
                        <>
                          <Icon name="Plus" />
                          Create Protocol
                        </>
                      )}
                    </button>
                  )}
                  
                  {isOwnProfile ? (
                    <button
                      className="user-profile-secondary-action-compact"
                      onClick={() => {
                        if (onSettingsClick) {
                          onSettingsClick();
                        }
                      }}
                    >
                      <Icon name="Settings" />
                      Settings
                    </button>
                  ) : (
                    <>
                      {onFollowToggle && (
                        <button
                          className={`user-profile-secondary-action-compact ${
                            isFollowPending ? 'user-profile-follow-btn-pending' : 
                            (isFollowing ? 'user-profile-follow-btn-unfollow' : 'user-profile-follow-btn-cta')
                          }`}
                          onClick={onFollowToggle}
                          disabled={followLoading}
                        >
                          {followLoading ? 'Loading...' : (isFollowPending ? 'Pending' : (isFollowing ? 'Unfollow' : 'Follow'))}
                        </button>
                      )}
                      {onMessage && (
                        <button
                          className="user-profile-secondary-action-compact"
                          onClick={onMessage}
                        >
                          <Icon name="MessageCircle" />
                          Message
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="user-profile-stats-container">
                <p className="user-profile-stats-title">Activity Stats</p>
                <div className="user-profile-stats-row">
                  <div className="user-profile-stat-item">
                    <span className="user-profile-stat-value">{activeProtocolsCount}</span>
                    <span className="user-profile-stat-label">Active Protocols</span>
                  </div>
                  <div className="user-profile-stat-item">
                    <span className="user-profile-stat-value">{loggingStreakDays}</span>
                    <span className="user-profile-stat-label">Logging Streak</span>
                  </div>
                  <div className="user-profile-stat-item">
                    <span className="user-profile-stat-value">{protocolUsersCount}</span>
                    <span className="user-profile-stat-label">Protocol Users</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section Divider */}
        <div className="user-profile-section-divider"></div>
        
        {/* Navigation Tabs */}
        <div className="user-profile-white-banner-nav">
          <div className="user-profile-white-banner-nav-content">
          <div className="user-profile-white-banner-nav-tabs">
            <button 
              className={`user-profile-white-banner-nav-btn ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
              title="View user activity and timeline"
            >
              <Icon name={getTabIcon('activity')} />
              <span>Activity{formatTabCount(getTabCount('activity'), 'activity') ? ` (${formatTabCount(getTabCount('activity'), 'activity')})` : ''}</span>
            </button>
            <button 
              className={`user-profile-white-banner-nav-btn ${activeTab === 'protocols' ? 'active' : ''}`}
              onClick={() => setActiveTab('protocols')}
              title="Browse user protocols and biohacks"
            >
              <Icon name={getTabIcon('protocols')} />
              <span>Protocols{formatTabCount(getTabCount('protocols'), 'protocols') ? ` (${formatTabCount(getTabCount('protocols'), 'protocols')})` : ''}</span>
            </button>
            <button 
              className={`user-profile-white-banner-nav-btn ${activeTab === 'biomarkers' ? 'active' : ''}`}
              onClick={() => setActiveTab('biomarkers')}
              title="View user biomarkers"
            >
              <Icon name={getTabIcon('biomarkers')} />
              <span>Biomarkers{formatTabCount(getTabCount('biomarkers'), 'biomarkers') ? ` (${formatTabCount(getTabCount('biomarkers'), 'biomarkers')})` : ''}</span>
            </button>
            <button 
              className={`user-profile-white-banner-nav-btn ${activeTab === 'wearables' ? 'active' : ''}`}
              onClick={() => setActiveTab('wearables')}
              title="Check user health metrics and wearables data"
            >
              <Icon name={getTabIcon('wearables')} />
              <span>Wearables{formatTabCount(getTabCount('wearables'), 'wearables') ? ` (${formatTabCount(getTabCount('wearables'), 'wearables')})` : ''}</span>
            </button>
            <button 
              className={`user-profile-white-banner-nav-btn ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
              title="View user posts and content"
            >
              <Icon name={getTabIcon('posts')} />
              <span>Posts{formatTabCount(getTabCount('posts'), 'posts') ? ` (${formatTabCount(getTabCount('posts'), 'posts')})` : ''}</span>
            </button>
            <button 
              className={`user-profile-white-banner-nav-btn ${activeTab === 'challenges' ? 'active' : ''}`}
              onClick={() => setActiveTab('challenges')}
              title="View user challenges and goals"
            >
              <Icon name={getTabIcon('challenges')} />
              <span>Challenges{formatTabCount(getTabCount('challenges'), 'challenges') ? ` (${formatTabCount(getTabCount('challenges'), 'challenges')})` : ''}</span>
            </button>
          </div>
          
          {/* Tab Privacy Control - Right Side */}
          {isOwnProfile && (
            <TabPrivacySettings
              userId={user?.id || user?.user_id}
              tabName={activeTab}
              currentPrivacyLevel={tabPrivacySettings[activeTab]}
              onPrivacyChange={onTabPrivacyChange}
            />
          )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="user-profile-content">
        {getTabContent()}
      </div>

      {/* Floating Tips Button */}
      <button
        className="user-profile-tips-button"
        onClick={() => setShowTipsModal(!showTipsModal)}
        title="Tips"
      >
        <Icon name="Info" />
        <span>Tips</span>
      </button>

      {/* Tips Modal */}
      {showTipsModal && (
        <div className="user-profile-tips-modal-overlay" onClick={() => setShowTipsModal(false)}>
          <div className="user-profile-tips-modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-profile-tips-modal-header">
              <h3>Tips</h3>
              <button
                className="user-profile-tips-modal-close"
                onClick={() => setShowTipsModal(false)}
                aria-label="Close tips"
              >
                <Icon name="X" />
              </button>
            </div>
            <div className="user-profile-tips-modal-content">
              {typeof getTipsContent() === 'string' ? <p>{getTipsContent()}</p> : getTipsContent()}
            </div>
          </div>
        </div>
      )}

      {/* Avatar Modal */}
      {showAvatarModal && (
        <div className="user-profile-avatar-modal-overlay" onClick={() => setShowAvatarModal(false)}>
          <div className="user-profile-avatar-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="user-profile-avatar-modal-close"
              onClick={() => setShowAvatarModal(false)}
              aria-label="Close avatar"
            >
              <Icon name="X" />
            </button>
            {user?.avatar_url && user?.avatar_url !== '/default-avatar.jpg' ? (
              <img 
                src={user.avatar_url} 
                alt={`${displayName}'s profile`}
                className="user-profile-avatar-modal-img"
              />
            ) : (
              <div className="user-profile-avatar-modal-fallback">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="user-profile-bottom-nav">
        <BottomNavigation />
      </div>
    </div>
  );
}

export default UserProfilePage;
